import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from '@reduxjs/toolkit';
import { apiClient, buildQueryString, withMinDelayThunk, withMinDelay } from '../../utils/api/';

const DEFAULT_WORKER_FILTERS = {
  type: 'ALL',
  search: '',
};

// Async Thunks

// Get all workers
export const fetchWorkers = createAsyncThunk(
  'workers/fetchWorkers',
  async (params = {}, { rejectWithValue, getState }) => {
    try {
      const { initialFetchDone } = getState().workers;
      const qs = buildQueryString(params);
      const url = qs ? `/workers?${qs}` : '/workers';
      const response = !initialFetchDone
        ? await withMinDelay(apiClient.get(url))
        : await apiClient.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch workers');
    }
  }
);

// Create a new worker
export const createWorker = createAsyncThunk(
  'workers/createWorker',
  withMinDelayThunk(async (workerData, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/workers', workerData);
      return response.data?.data || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create worker');
    }
  })
);

// Update an existing worker
export const updateWorker = createAsyncThunk(
  'workers/updateWorker',
  withMinDelayThunk(async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(`/workers/${id}`, data);
      return response.data?.data || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update worker');
    }
  })
);

// Delete a worker
export const deleteWorker = createAsyncThunk(
  'workers/deleteWorker',
  withMinDelayThunk(async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/workers/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete worker');
    }
  })
);

// Test Worker URL
export const testWorkerUrl = createAsyncThunk(
  'workers/testWorkerUrl',
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/workers/test-url', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to test URL');
    }
  }
);

// Fetch Prebuild Workers
export const fetchPrebuildWorkers = createAsyncThunk(
  'workers/fetchPrebuildWorkers',
  // eslint-disable-next-line no-unused-vars
  async (_, { rejectWithValue }) => {
    // try {
      // Mocking for now as per instructions
      return {
        data: [
          { id: '1', name: 'Amazon Price Tracker', description: 'Tracks price of a product on Amazon' },
          { id: '2', name: 'Stock Ticker', description: 'Gets latest stock price' },
          { id: '3', name: 'Weather Alert', description: 'Checks weather conditions' },
        ]
      };
    // } catch (error) {
    //   return rejectWithValue(error.response?.data || 'Failed to fetch prebuild workers');
    // }
  }
);

// Validate Worker Response
export const validateWorkerResponse = createAsyncThunk(
  'workers/validateWorkerResponse',
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/workers/validate-response', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Validation failed');
    }
  }
);

const initialState = {
  items: [],
  pageInfo: null,
  filters: DEFAULT_WORKER_FILTERS,
  typeFilterValue: DEFAULT_WORKER_FILTERS.type,
  searchFilterValue: DEFAULT_WORKER_FILTERS.search,
  loading: false,
  error: null,
  initialFetchDone: false,
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
};

const workersSlice = createSlice({
  name: 'workers',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      const nextFilters = {
        ...DEFAULT_WORKER_FILTERS,
        ...(state.filters || {}),
        ...action.payload,
      };

      state.filters = nextFilters;
      state.typeFilterValue = nextFilters.type;
      state.searchFilterValue = nextFilters.search;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetStatus: (state) => {
      state.status = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Workers
      .addCase(fetchWorkers.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(fetchWorkers.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.pageInfo = action.payload.pageInfo || null;
        state.initialFetchDone = true;
        state.status = 'succeeded';
      })
      .addCase(fetchWorkers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      })

      // Create Worker
      .addCase(createWorker.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(createWorker.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload);
        state.status = 'succeeded';
      })
      .addCase(createWorker.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      })

      // Update Worker
      .addCase(updateWorker.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(updateWorker.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex((item) => item._id === action.payload._id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        state.status = 'succeeded';
      })
      .addCase(updateWorker.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      })

      // Delete Worker
      .addCase(deleteWorker.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(deleteWorker.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter((item) => item._id !== action.payload);
        state.status = 'succeeded';
      })
      .addCase(deleteWorker.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      });
  },
});

export const {
  clearError,
  resetStatus,
  setFilters,
} = workersSlice.actions;

// Selectors
export const selectAllWorkers = (state) => state.workers.items;
export const selectWorkersLoading = (state) => state.workers.loading;
export const selectWorkersInitialFetchDone = (state) => state.workers.initialFetchDone;
export const selectWorkersStatus = (state) => state.workers.status;
const selectWorkersState = (state) => state.workers;

export const selectWorkersFilters = createSelector(
  [selectWorkersState],
  (workersState) => {
    if (workersState.filters) {
      return workersState.filters;
    }

    return {
      type: workersState.typeFilterValue ?? DEFAULT_WORKER_FILTERS.type,
      search: workersState.searchFilterValue ?? DEFAULT_WORKER_FILTERS.search,
    };
  }
);
export const selectWorkersPageInfo = (state) => state.workers.pageInfo;

export default workersSlice.reducer;
